// @flow

import * as React from 'react';
import {useState, useRef} from 'react';
import Text from 'sail/Text/Text';
import ContextualPopover from 'sail/Layer/ContextualPopover';
import Box from 'sail/Box';
import {InlineLink} from 'sail/InlineLink';
import './Glossary.scss';
import {useIntl} from 'react-intl';
import GLOSSARY from './glossary.yaml';
import {definitionMessages, termMessages} from './glossaryMessages';

type InlineTooltipProps = {|
  context: React.Node,
  tooltipContents: string,
|};

const InlineTooltip = ({context, tooltipContents}: InlineTooltipProps) => {
  // Sail's TooltipContainer uses a <div>. A <div> cannot be nested
  // inside of a <p>, which is why this custom tooltip had to be
  // created. It uses ContextualPopover to avoid any problems due to
  // clipping from overflow styles or z-index issues. (This is what Sail's
  // TooltipContainer uses under the hood also).

  const contextRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <span>
      <span
        ref={contextRef}
        onMouseOver={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {context}
      </span>
      {contextRef.current && (
        <ContextualPopover
          context={{element: contextRef.current}}
          contextMeetingPoint="top"
          layerMeetingPoint="bottom"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          shown={showTooltip}
          trapFocus={false}
        >
          <Box style={{maxWidth: '300px'}} padding={16}>
            <Text lineHeight={20}>{tooltipContents}</Text>
          </Box>
        </ContextualPopover>
      )}
    </span>
  );
};

const ErrorObj = ({error}: {error: string}) => {
  // eslint-disable-next-line no-console
  console.error(error);
  return (
    <Text color="red" weight="bold">
      {error}
    </Text>
  );
};

type GlossaryProps = {|
  term: string,
  children: React.Node,
|};

export const Glossary = ({term, children}: GlossaryProps) => {
  const intl = useIntl();
  const glossaryEntry = GLOSSARY[term];

  if (!glossaryEntry) {
    if (process.env.NODE_ENV === 'development') {
      const error = `Unrecognized glossary term: ${term}. Is it misspelled? Was it not added to the definitions list?`;
      return <ErrorObj error={error} />;
    }
    return children;
  }

  if (!definitionMessages[term] || !termMessages[term]) {
    if (process.env.NODE_ENV === 'development') {
      const error = `Message gen missing! You probably just need to run \`cd ~/stripe/pay-server/docs && yarn generate-glossary-messages\``;
      return <ErrorObj error={error} />;
    }
    return children;
  }

  const tooltipContents = intl.formatMessage(definitionMessages[term]);
  const context = glossaryEntry.link ? (
    <InlineLink
      href={glossaryEntry.link}
      className="Glossary-term no-api-tag"
      title={intl.formatMessage(termMessages[term])}
    >
      {children}
    </InlineLink>
  ) : (
    <Text color="blue" weight="medium" className="Glossary-term">
      {children}
    </Text>
  );

  return <InlineTooltip context={context} tooltipContents={tooltipContents} />;
};
